import dayjs from 'dayjs'
import cloneDeep from 'lodash/cloneDeep'
import groupBy from 'lodash/groupBy'
import { Community } from '../../models'
import { SubmittableEventCreateSchema, SubmittableEventUpdateSchema } from '../../schemas/events/eventSchema'
import {
  EventTypes,
  RepeatingPatterns,
  RepeatingPatternsType,
  Weekdays,
  WeekdaysType,
  repeatingPatternIsCustom,
} from '../../util/constants'
import {
  dayOfTheWeekInViewerZone,
  eventTimeZone,
  formatDate,
  formatDateInViewerZone,
  formatTimeInViewerZone,
} from '../../util/dateTime'
import { capitalizeFirstLetter } from '../../util/string'
import EventSeries from './EventSeries'
import EventStartEndDateTime from './EventStartEndDateTime'
import { Event } from './model'

// Standalone helper to flatten nested object fields on plain objects
// Replaces { contact: { name: 'viktor' } } with { contactName: 'viktor' }
const flatObjectFields = (obj: Record<string, unknown>, fieldName: string): void => {
  const nested = obj[fieldName]
  if (!nested || typeof nested !== 'object') {
    return
  }
  Object.entries(nested).forEach(([key, value]) => {
    obj[`${fieldName}${capitalizeFirstLetter(key)}`] = typeof value === 'undefined' ? null : value
  })
  delete obj[fieldName]
}

// The flattened tempPlace.* keys as they go over the wire, i.e. what
// flatObjectFields(eventObj, 'tempPlace') + (…, 'tempPlaceAddress') produce and
// SubmittableEvent{Create,Update}Schema declares. Used to clear a typed venue
// when the submitter picks a directory Place instead (see prepareToSubmit).
const TEMP_PLACE_WIRE_FIELDS = [
  'tempPlaceName',
  'tempPlaceUrl',
  'tempPlaceAddressStreetAddress',
  'tempPlaceAddressStreetAddress2',
  'tempPlaceAddressCity',
  'tempPlaceAddressState',
  'tempPlaceAddressZipCode',
] as const

const ORDINAL_NUMBERS = ['first', 'second', 'third', 'fourth', 'fifth']
export const ordinalWeekOfMonth = (date: Date): string => {
  const dayOfMonth = parseInt(dayjs(date).format('D'))
  return ORDINAL_NUMBERS[Math.floor((dayOfMonth - 1) / 7)]
}

const dayOfMonth = (date: Date): string => dayjs(date).format('Do')
type RepeatDays = { [key in WeekdaysType]: boolean }

export const initRepeatDays = (repeatPattern: RepeatingPatternsType, startAt: Date): RepeatDays | null => {
  if (repeatPattern !== RepeatingPatterns.WEEKLY) {
    return null
  }
  const repeatDays: Partial<RepeatDays> = {}
  Object.values(Weekdays).forEach(day => (repeatDays[day] = false))
  repeatDays[dayOfTheWeekInViewerZone(startAt) as WeekdaysType] = true
  return repeatDays as RepeatDays
}

export type OccurrencesPreviewInput = {
  startAtDate?: Date
  startAtTime?: string
  endAtTime?: string
  allDay?: boolean
  eventType?: string
  repeats?: string
  repeatDays?: Partial<Record<WeekdaysType, boolean>>
  repeatStopsOn?: Date
  timeZone?: string
  placeId?: string | null
  primaryCommunityId?: string | null
}

type OccurrencesPreviewModel = OccurrencesPreviewInput & {
  placeId?: string
  primaryCommunityId?: string
}

export const occurrencesInputFromModel = (model: OccurrencesPreviewModel): OccurrencesPreviewInput => {
  return {
    startAtDate: model.startAtDate,
    startAtTime: model.startAtTime,
    endAtTime: model.endAtTime,
    allDay: model.allDay,
    eventType: model.eventType,
    repeats: model.repeats,
    repeatDays: model.repeatDays,
    repeatStopsOn: model.repeatStopsOn,
    timeZone: model.timeZone,
    placeId: model.placeId ?? null,
    primaryCommunityId: model.primaryCommunityId ?? null,
  }
}

// Phase 9 (design rev.5 §1 Outbound). Parse the server's naive wall-clock
// "YYYY-MM-DDTHH:MM" (EventType.start_at_local / end_at_local — already in the
// event's venue-resolved DISPLAY zone, Phase 6) into a form-model Date using
// LOCAL construction. This is deliberately NOT `new Date(str)`: a bare
// "YYYY-MM-DDTHH:MM" with no offset is parsed inconsistently across JS engines
// (some as local, some as UTC), and NOT `new Date(<ISO instant>)` (model.ts:
// 20-27) which re-zones the absolute instant into the EDITOR's browser zone —
// the exact bug this project eliminates. `new Date(y, m-1, d, h, min)` builds a
// Date whose *local* wall-clock is exactly the server's venue wall-clock, so the
// editor sees what the public feed shows and a no-op save re-derives the same
// *_local (Phase 8a) -> EventUpdate#resolve re-resolves the same zone -> the
// same stored UTC instant (round-trip: out == in). Returns undefined for a
// blank / malformed string so the caller falls back to the legacy Date.
// Exported for Phase 11: useServerOccurrencesPreview parses the server's
// venue-zone wall-clock occurrence strings into LOCAL Dates the same way the
// edit-load path does, so the live preview shows the venue wall-clock the
// server will store (NOT a browser re-zoning of an absolute instant — the
// #877 bug). Same parser, one definition.
const LOCAL_WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/
export const parseLocalWallClock = (value?: string | null): Date | undefined => {
  if (!value) {
    return undefined
  }
  const match = LOCAL_WALL_CLOCK.exec(value)
  if (!match) {
    return undefined
  }
  const [, y, m, d, h, min] = match
  return new Date(Number(y), Number(m) - 1, Number(d), Number(h), Number(min), 0)
}

type EditFormDateTimeFields = {
  startAtDate?: Date
  startAtTime: string
  endAtDate?: Date
  endAtTime: string
}

// Build the edit form's split date/time fields (startAtDate/startAtTime/
// endAtDate/endAtTime) from the loaded event. Prefers the server's venue-zone
// wall-clock (startAtLocal/endAtLocal, Phase 6) via LOCAL construction; falls
// back to the legacy ISO-instant Date (model.ts) when *_local is absent (old
// cached server — additive rollout safety, design §Rollout). The split fields
// stay plain `Date`s built through EventStartEndDateTime, so the form model's
// Date and the eventSchema.ts dayjs(startAt) validators are untouched (design
// §1 / §1a — no relocated #877 split-Date wall). Scope: the edit-load path
// only. `model.ts`'s `new Date(<ISO instant>)` is intentionally left intact —
// it is `originalEvent.startAt`/`endAt`, the absolute instant other model
// consumers (e.g. eventsSummary) rely on; only the edit form's date/time field
// SEED is taken from *_local here.
export const editFormDateTimeFields = (event: {
  startAtLocal?: string | null
  endAtLocal?: string | null
  startAt?: Date
  endAt?: Date
}): EditFormDateTimeFields => {
  const start = parseLocalWallClock(event.startAtLocal) ?? event.startAt
  const end = parseLocalWallClock(event.endAtLocal) ?? event.endAt ?? start
  const dt = new EventStartEndDateTime({ startAt: start as Date, endAt: end as Date })
  return {
    startAtDate: dt.startAtDate,
    startAtTime: dt.startAtTime,
    endAtDate: dt.endAtDate,
    endAtTime: dt.endAtTime,
  }
}

export const eventsSummary = (event: Event): string | null => {
  if (!event.startAt || !event.eventType) {
    return null
  }

  const isAllDay = event.allDay

  if (event.eventType === EventTypes.SINGLE_EVENT) {
    if (isAllDay) {
      // The event is all day on September 30, 2021.
      return `The event is all day on ${formatDateInViewerZone(event.startAt)}.`
    } else if (!event.endAt) {
      // The event is at 12:00pm on September 30, 2021.
      return `The event is at ${formatTimeInViewerZone(event.startAt)} on ${formatDateInViewerZone(event.startAt)}.`
    } else {
      // The event is from 12:00pm to 4:00pm on September 30, 2021.
      const isSameDay = dayjs(event.startAt).isSame(event.endAt, 'day')

      if (isSameDay) {
        return `The event is from ${formatTimeInViewerZone(event.startAt)} to ${formatTimeInViewerZone(event.endAt)} on ${formatDateInViewerZone(event.startAt)}.`
      } else {
        return `The event is from ${formatTimeInViewerZone(event.startAt)} ${formatDateInViewerZone(event.startAt)} to ${formatTimeInViewerZone(event.endAt)} ${formatDateInViewerZone(event.endAt)}.`
      }
    }
  }

  if (repeatingPatternIsCustom(event.repeats)) {
    return null
  }

  let summary
  const timeDescription = isAllDay ? '(all day)' : `at ${formatTimeInViewerZone(event.startAt)}`

  switch (event.repeats) {
    case RepeatingPatterns.DAILY:
    case RepeatingPatterns.REQUEST_SCHEDULE:
      // An event every day that begins on September 30, 2021 at 12:00pm, repeating until October 2, 2021
      summary = `An all-day event every day that begins on ${formatDateInViewerZone(event.startAt)}`
      if (!isAllDay) {
        summary = `An event every day that begins on ${formatDateInViewerZone(event.startAt)} ${timeDescription}`
      }
      break
    case RepeatingPatterns.WEEKLY:
      // An event every Tuesday and Wednesday that begins at 9:00am on August 10th, 2021, repeating until September 7, 2021
      const daysOfTheWeek = Object.entries(event.repeatDays || {})
        .filter(([_key, value]) => value)
        .map(([key, _value]) => key)
      let daysOfTheWeekJoined
      if (daysOfTheWeek.length === 0) {
        daysOfTheWeekJoined = dayOfTheWeekInViewerZone(event.startAt)
      } else {
        daysOfTheWeekJoined = daysOfTheWeek.reduce(
          // [1, 2, 3, 4, 5]
          // => "1, 2, 3, 4 and 5"
          (sentence, current, currentIdx, days) => sentence + (currentIdx < days.length - 1 ? ', ' : ' and ') + current,
        )
      }
      if (isAllDay) {
        summary = `An all-day event every ${daysOfTheWeekJoined} that begins on ${formatDateInViewerZone(event.startAt)}`
      } else {
        summary = `An event every ${daysOfTheWeekJoined} that begins ${timeDescription} on ${formatDateInViewerZone(event.startAt)}`
      }
      break
    case RepeatingPatterns.MONTHLY_SAME_DATE:
      // An event every 12th of the month, repeating until January 14, 2022
      summary = isAllDay
        ? `An all-day event every ${dayOfMonth(event.startAt)} of the month`
        : `An event every ${dayOfMonth(event.startAt)} of the month`
      break
    case RepeatingPatterns.MONTHLY_NTH_DOW:
      // An event every third Wednesday of the month, repeating until January 14, 2022
      summary = isAllDay
        ? `An all-day event every ${ordinalWeekOfMonth(event.startAt)} ${dayOfTheWeekInViewerZone(event.startAt)} of the month`
        : `An event every ${ordinalWeekOfMonth(event.startAt)} ${dayOfTheWeekInViewerZone(event.startAt)} of the month`
      break
    default:
      return 'Custom schedule'
  }

  if (event.repeatStopsOn) {
    summary += `, repeating until ${formatDateInViewerZone(event.repeatStopsOn)}`
  }
  return summary
}

export const eventDates = (event: Event): EventStartEndDateTime[] => {
  const result: EventStartEndDateTime[] = []

  const nextStartAtGenerator = new EventSeries({ repeats: event.repeats, startAt: event.startAt })
  const nextEndAtGenerator = new EventSeries({ repeats: event.repeats, startAt: event.endAt })

  let currentStartAt = event.startAt
  let currentEndAt = event.endAt
  for (let i = 0; i < EventSeries.MAX_EVENTS_IN_SERIES; i++) {
    result.push(new EventStartEndDateTime({ startAt: currentStartAt, endAt: currentEndAt }))
    currentStartAt = nextStartAtGenerator.next(currentStartAt)
    currentEndAt = nextEndAtGenerator.next(currentEndAt)
    if (event.repeatStopsOn && dayjs(currentStartAt).startOf('day').isAfter(event.repeatStopsOn)) {
      break
    }
  }

  return result
}

// This method is used to make sure that endAt is after startAt.
// This happens when user sets endAt time to be before startAt. (e.g. startAt = 10pm, endAt = 2am)
// endAt at 2am is actually supposed to be 2am the next day.
// This can also happen when user changes startAt to a previous date.
const fixEndAtDate = (startEndDateTime: EventStartEndDateTime) => {
  startEndDateTime.endAtDate = startEndDateTime.startAtDate
  if (dayjs(startEndDateTime.endAt).isBefore(dayjs(startEndDateTime.startAt))) {
    startEndDateTime.endAt = dayjs(startEndDateTime.endAt).add(1, 'day').toDate()
  }
}

// Phase 11 (design rev.5 §1/§3): SINGLE SOURCE OF TRUTH for "form split
// date/time fields -> the RAW naive wall-clock the server receives". Used by
// BOTH prepareToSubmit (the submit payload's startAtLocal/endAtLocal) AND
// useServerOccurrencesPreview (the previewEventOccurrences query inputs), so
// the live occurrences preview is fed the IDENTICAL wall-clock that is
// submitted — the preview provably matches what EventCreate stores (no
// client-side drift; the frontend mirror of the backend
// Events::CreateLeadFromWallClock single-source extraction). Mirrors
// prepareToSubmit's reconstruction EXACTLY:
//   * merge startAtDate + startAtTime -> start; startAtDate + endAtTime ->
//     end (the form has no end-DATE field — end's date follows the start's).
//   * format with LOCAL getters BEFORE any all-day / fixEndAtDate (+1-day)
//     browser-zone normalization — the server is the authoritative
//     overnight/all-day normalizer for the *_local path (design §4). getMonth
//     is 0-indexed (+1); UTC getters / toISOString would reintroduce the
//     off-by-one this project eliminates.
const padTwo = (n: number): string => String(n).padStart(2, '0')
const toLocalWallClock = (d: Date): string =>
  `${d.getFullYear()}-${padTwo(d.getMonth() + 1)}-${padTwo(d.getDate())}T${padTwo(d.getHours())}:${padTwo(d.getMinutes())}`

export const formRawWallClock = (event: {
  startAtDate?: Date
  startAtTime?: string
  endAtTime?: string
}): { startAtLocal?: string; endAtLocal?: string } => {
  if (!event.startAtDate) {
    // INTENTIONAL divergence from the pre-Phase-11 inline code for the
    // degenerate no-startAtDate state: the old code could emit a retained
    // cloneDeep instant on the edit path; emitting nothing here is more
    // correct (jest-covered) and the create form ALWAYS sets startAtDate.
    // Do NOT "restore" the old retained-instant behavior.
    return {}
  }
  // EXACTLY prepareToSubmit's merge: start = startAtDate(+startAtTime); end =
  // startAtDate(+endAtTime) — and endAt is ALWAYS set (defaults to startAtDate
  // when no endAtTime), so endAtLocal is emitted whenever startAtDate is
  // present, matching the pre-Phase-11 submit payload byte-for-byte. (Do NOT
  // "optimize" to drop endAtLocal when endAtTime is blank — that would change
  // the submit behavior the server's *_local path depends on.)
  const merged = new EventStartEndDateTime({ startAt: event.startAtDate, endAt: event.startAtDate })
  if (event.startAtTime) {
    merged.startAtTime = event.startAtTime
  }
  if (event.endAtTime) {
    merged.endAtTime = event.endAtTime
  }
  return {
    startAtLocal: toLocalWallClock(merged.startAt),
    endAtLocal: toLocalWallClock(merged.endAt),
  }
}

// The weekly picker stores repeatDays as a boolean map ({ Monday: true, ... })
// which drives the summary sentence. The server's repeat_days arg wants an
// array of lowercased weekday names. Only meaningful for WEEKLY; other patterns
// send []. Mirrors the summary's selected-day list (helpers.ts eventsSummary).
export const selectedRepeatDays = (repeatDays?: Partial<Record<WeekdaysType, boolean>>): string[] =>
  Object.entries(repeatDays || {})
    .filter(([, selected]) => selected)
    .map(([day]) => day.toLowerCase())

// Set startAt/endAt from the split form fields (startAtDate + startAtTime /
// endAtTime). Mutates `event`.
const mergeFormStartEnd = (event: Event): void => {
  if (event.startAtDate) {
    const merged = new EventStartEndDateTime({ startAt: event.startAtDate, endAt: event.startAtDate })
    if (event.startAtTime) {
      merged.startAtTime = event.startAtTime
    }
    event.startAt = merged.startAt

    if (event.endAtTime) {
      merged.endAtTime = event.endAtTime
    }
    event.endAt = merged.endAt
  }
}

// A copy of a form model whose startAt/endAt are the date and times currently
// in the form (for summaries of what a save will store).
export const withFormStartEnd = (model: Event): Event => {
  const event = { ...model } as Event
  mergeFormStartEnd(event)
  return event
}

export const prepareToSubmit = (originalEvent: Event): Event => {
  const event = cloneDeep(originalEvent)

  // Phase 8a/11: derive the RAW naive wall-clock (startAtLocal/endAtLocal)
  // from the split form fields via the shared formRawWallClock — BEFORE the
  // all-day block (~below) and fixEndAtDate (the +1-day overnight mutation),
  // both browser-zone. The server is the authoritative overnight/all-day
  // normalizer for the *_local path; the SAME helper feeds the live
  // occurrences preview so preview == submitted (design §1/§3).
  const rawWallClock = formRawWallClock(event)

  // Reconstruct startAt/endAt from split form fields (startAtDate + startAtTime, etc.)
  // The form stores date and time separately; merge them before submission.
  // Always seed `endAt` from the form `startAtDate` so the end date follows the
  // start date — the form has no end-date field, only an end-time field. Using a
  // persisted `event.endAt` here would let the user end up with end_at on the
  // pre-edit date with the new end time merged on top.
  mergeFormStartEnd(event)

  // Phase 8a (design rev.5 §1, §1a): the *_local strings come from the shared
  // formRawWallClock above (RAW merged date+time, pre-all-day/pre-fixEndAtDate
  // — those normalize in the BROWSER zone; the server re-applies them in the
  // resolved zone for the *_local path). Additive: event.startAt/endAt (the
  // legacy Date) keep serializing unchanged; the form model keeps its Date so
  // the eventSchema.ts dayjs(startAt) validators are untouched.
  if (rawWallClock.startAtLocal) (event as unknown as Record<string, unknown>).startAtLocal = rawWallClock.startAtLocal
  if (rawWallClock.endAtLocal) (event as unknown as Record<string, unknown>).endAtLocal = rawWallClock.endAtLocal

  if (event.eventType === EventTypes.SINGLE_EVENT) {
    ;(['repeats', 'repeatStopsOn'] as const).forEach(field => delete event[field])
  }

  // Handle all-day events: set times to start/end of day
  if (event.allDay && event.startAt) {
    event.startAt = dayjs(event.startAt).startOf('day').toDate()
    if (event.endAt) {
      event.endAt = dayjs(event.endAt).endOf('day').toDate()
    } else {
      // If no end date, set end to end of start day
      event.endAt = dayjs(event.startAt).endOf('day').toDate()
    }
    if (event.occurrences) {
      event.occurrences.forEach(occurrence => {
        occurrence.startAt = dayjs(occurrence.startAt).startOf('day').toDate()
        occurrence.endAt = dayjs(occurrence.endAt || occurrence.startAt).endOf('day').toDate()
      })
    }
  }

  // Convert organizerType + selectedOrganizationId to submittedOnBehalfOfOrgId
  if (event.organizerType === 'organization' && event.selectedOrganizationId) {
    event.submittedOnBehalfOfOrgId = event.selectedOrganizationId
  } else {
    event.submittedOnBehalfOfOrgId = null
  }
  // Remove local-only fields
  delete event.organizerType
  delete event.selectedOrganizationId
  // Remove the loaded organization object (only send the ID)
  delete event.submittedOnBehalfOfOrg

  // Flatten nested object fields (use standalone helper since cloneDeep creates plain object)
  const eventObj = event as unknown as Record<string, unknown>
  flatObjectFields(eventObj, 'image')
  flatObjectFields(eventObj, 'organizer')
  flatObjectFields(eventObj, 'tempPlace')
  flatObjectFields(eventObj, 'tempPlaceAddress')

  // Venue exclusivity — the event's venue is EITHER a directory Place
  // (`placeId`) or a typed-in one (`tempPlace*`), never both. `useTempPlace`
  // carries that intent but is a FORM-ONLY field: neither Submittable schema
  // declares it, so the `.clean()` below strips it and the server never sees it
  // (Types::EventType re-derives it as `place_id.blank?`). The eventSchema.ts
  // autoValues that encode the exclusivity hang off EventFormSchema /
  // EventScaffoldSchema, which never clean this wire payload — so the intent has
  // to be applied here, on the last hop before the mutation. Without it the
  // payload carried BOTH and the server assigned BOTH (support conversation
  // #2410):
  //   - checking "my venue isn't in the CivicLift Places directory" kept the
  //     previously-selected place_id, so the reloaded form re-derived
  //     useTempPlace = false and the typed venue silently reverted — a dead end
  //     with no way out of a misnamed directory venue; and
  //   - selecting a directory venue left a stale temp_place_name behind, and
  //     temp_place_name WINS in Event#place_title, so the public feed kept
  //     showing the old typed name while the form showed the Place.
  // Explicit nulls (not deleted keys) are required: the mutation only clears a
  // column for an argument that is actually present in the request.
  if (event.useTempPlace) {
    eventObj.placeId = null
  } else {
    TEMP_PLACE_WIRE_FIELDS.forEach(field => {
      eventObj[field] = null
    })
  }

  // PHASE 8 WARNING: the server now ALSO re-applies overnight/all-day in the
  // RESOLVED (venue) zone for the *_local path (app/services/events/
  // normalize_wall_clock_span.rb). When Phase 8 starts deriving/sending
  // startAtLocal/endAtLocal, derive them from the RAW pre-fixEndAtDate /
  // pre-all-day wall-clock (the raw form date+time fields) — NOT from the
  // post-normalization event.startAt/endAt below, which are already
  // browser-zone-normalized. Feeding browser-zone-normalized values to the
  // server, which re-normalizes in a DIFFERENT zone, silently corrupts spans
  // for cross-zone submitters. (Comment only — do not change this logic here.)
  fixEndAtDate(event)
  event.occurrences && event.occurrences.forEach(fixEndAtDate)

  const serialized = JSON.parse(JSON.stringify(event))
  // Phase 8a (design rev.5 §3, Task 8.4): the SERVER now expands repeating series
  // in the resolved venue zone (DST-correct; EventSeriesCreator#expand_on_server?,
  // gated on occurrences.blank?). Stop sending client-computed occurrences so the
  // server-expansion path is taken; the recurrence rule (repeats / repeatStopsOn /
  // repeatDays) is still serialized and is all the server needs. The form-model
  // `occurrences` is intentionally left intact (it still feeds the live
  // EventOccurrences preview — Phase 11 owns replacing that data source). Old
  // cached clients that lack this line still send occurrences and hit the
  // unchanged legacy server path (additive rollout, design §Rollout).
  delete serialized.occurrences
  // Convert the picker's boolean map to the wire array the server expands from
  // (repeat_days). Non-weekly patterns send [] (start-weekday-only expansion).
  serialized.repeatDays =
    event.repeats === RepeatingPatterns.WEEKLY ? selectedRepeatDays(event.repeatDays) : []
  if (serialized.subcategory === '') serialized.subcategory = null
  if (serialized.repeats === RepeatingPatterns.REQUEST_SCHEDULE) serialized.repeats = RepeatingPatterns.DAILY
  // Check id directly since cloneDeep creates plain object without isNew getter
  const isNew = event.id == null
  return isNew
    ? SubmittableEventCreateSchema.clean(serialized, { removeEmptyStrings: false })
    : SubmittableEventUpdateSchema.clean(serialized, { removeEmptyStrings: false })
}
// [
//   mon1, mon2, mon3, tue1, tue2
// ] => {
//   'September 25, 2021': [mon1, mon2, mon3];
//   'September 26, 2021': [tue1, tue2]
// }
type GroupEventsByDayReturnType = {
  [key: string]: Event[]
}
export const groupEventsByDay = (events: Event[]): GroupEventsByDayReturnType =>
  groupBy(events, (event: Event) => formatDate(event.startAt, eventTimeZone(event)))

type EventsToFullSizeAndTableEventsReturnType = {
  fullSizeEvents: Event[]
  tableEvents: Event[]
}
export const splitEventsToFullSizeAndTableEvents = (sameDayEvents: Event[]): EventsToFullSizeAndTableEventsReturnType => {
  let fullSizeEvents: Event[] = []
  const tableEvents: Event[] = []
  sameDayEvents.forEach(event => {
    if (!isDailyEvent(event)) return fullSizeEvents.push(event)
    if (isFirstOccurrenceOfDailyEvent(event)) return fullSizeEvents.push(event)
    tableEvents.push(event)
  })
  // move up to first 3 table events to full size events
  if (fullSizeEvents.length === 0) fullSizeEvents = tableEvents.splice(0, 3)
  return { fullSizeEvents, tableEvents }
}

const isDailyEvent = (event: Event): boolean => event.repeats === RepeatingPatterns.DAILY

const isFirstOccurrenceOfDailyEvent = (dailyEvent: Event): boolean => !dailyEvent.day || dailyEvent.day.startsWith('Day 1 of')

export type eventIdToCommunityIndexMapType = {
  [key: string]: number
}
export const mapEventIdToCommunityIndex = (communities: Community[], events: Event[]): eventIdToCommunityIndexMapType => {
  const eventIdToCommunityIndexMap: eventIdToCommunityIndexMapType = {}

  events.forEach(event => {
    const communityIndex = communities.findIndex(c => c.id === event.community.id) + 1
    eventIdToCommunityIndexMap[event.id] = communityIndex
  })

  return eventIdToCommunityIndexMap
}
